import { useCallback, useState } from 'react';
import { toast } from 'sonner';

import { backendApis } from '@/backendApis';
import { trackEvent } from '@/utils/analytics';
import { errorToast } from '@/utils/toast';

import { type InviteRequest } from './referral.types';

const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const useSendInvites = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const sendInvites = useCallback(async (emails: string[]) => {
    setLoading(true);
    setError(null);

    try {
      if (emails.length === 0) {
        throw new Error('Please enter at least one email address');
      }

      const invalidEmails = emails.filter((email) => !isValidEmail(email));
      if (invalidEmails.length > 0) {
        throw new Error(`Invalid email addresses: ${invalidEmails.join(', ')}`);
      }

      const request: InviteRequest = { emails };
      const response = await backendApis.invite.sendInvites(request);

      if (response.success) {
        toast.success('Invites Sent', {
          description: 'Successfully sent your invitations!',
        });

        trackEvent('INVITE_EMAILS_SENT', {
          invited_emails: emails,
        });
      } else {
        throw new Error(response.message || 'Failed to send invites');
      }

      return response;
    } catch (error) {
      console.error('Failed to send invites:', error);
      setError(error as Error);
      errorToast(error as Error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  return { sendInvites, loading, error };
};
